import { Navigate, Route, Routes } from 'react-router-dom'
import { useCallback, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import OperatorSignUp from '@/features/auth/pages/Signup'
import { LoginPayload, useLogin, useGetUserSession } from '@/features/auth/hooks/useAuth'
import OpearatorLogin from '@/features/auth/pages/Login'
import { TermsOfServicePage } from '@/features/auth/pages/TermsOfServicePage'
import useI18n from '@/shared/hooks/useI18n'
import { SessionContext } from '@/shared/hooks/useSession'
import App from './App'
import AcceptInvitation from '@/features/auth/pages/AcceptInvitation'
import { LoadingScreen } from '@/shared/components'
import ForgotPasswordPage from '@/features/auth/pages/forgot-password'
import ResetPasswordPage from '@/features/auth/pages/reset-password'

export default function Root() {
  const { handleChangeLanguage } = useI18n()
  const { mutateAsync: login, isPending: isLoggingIn } = useLogin()

  const userSessionQuery = useGetUserSession()
  const session = userSessionQuery.data
  const isAuthCheckComplete = userSessionQuery.isFetched
  const isAuthenticated = !!session
  const doLogin = useCallback(
    async (payload: LoginPayload) => {
      await login(payload)
      userSessionQuery.refetch()
    },
    [login, userSessionQuery]
  )

  const onSignupSuccess = useCallback(
    ({ email, password }: { email: string; password: string }) => {
      doLogin({ email, password })
    },
    [doLogin]
  )

  useEffect(() => {
    const hasExistingSessionButRefetchFailed = userSessionQuery.status === 'error' && session

    // this happens when the user is logged in but the session is expired
    // we need to reload the page to get a new session
    if (hasExistingSessionButRefetchFailed) {
      window.location.reload()
    } else if (session) {
      handleChangeLanguage(session.language)
      Sentry.setUser({
        id: session.id,
        isEmailVerified: session.isEmailVerified,
        organizationId: session.organization.id
      })
    }
  }, [handleChangeLanguage, session, userSessionQuery.status])

  if (!isAuthCheckComplete) {
    return <LoadingScreen />
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="signup" element={<OperatorSignUp onSuccess={onSignupSuccess} />} />
        <Route
          path="accept-invitation"
          element={<AcceptInvitation onSuccess={onSignupSuccess} />}
        />
        <Route
          path="login"
          element={<OpearatorLogin login={doLogin} isLoggingIn={isLoggingIn} />}
        />
        <Route path="forgot-password" element={<ForgotPasswordPage />} />
        <Route path="reset-password" element={<ResetPasswordPage />} />
        <Route path="terms-of-service" element={<TermsOfServicePage />} />
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    )
  }

  return (
    <SessionContext.Provider value={{ session }}>
      <App />
    </SessionContext.Provider>
  )
}
